import { PhotoAPI } from '../../api/api-switcher'
import { trackFirstPhotoUploadSuccess } from './first-upload-tracking'
import { UploadResult, UploadStates, UploadResults, upload_error } from '../../utils/little-parts'

export const UPLOAD_FILETYPE = Object.freeze(['image/jpeg', 'image/png'])
const MAX_ERROR_COUNT = 4

export const imageFileToWidthHeight = (file: File): Promise<{width: number, height: number}> => {
  return new Promise((resolve, reject)=> {
    const img = new Image()
    img.onload = ()=> {
      resolve({width: img.naturalWidth, height: img.naturalHeight})
      URL.revokeObjectURL(img.src)
    }
    img.onerror = e=> {
      reject(e)
      // img.src = '' するとエラーが発生しまくりリソースを食う
      URL.revokeObjectURL(img.src)
    }
    img.src = URL.createObjectURL(file)
  })
}

const fileNameToTakenTime = (name: string): Date | undefined => {
  const time_regex = /(\d{4}-\d{2}-\d{2})_(\d{2}-\d{2}-\d{2}\.\d{3})/
  const match = name.match(time_regex)
  if (!match) return undefined
  const [, day_str, time_str] = match
  // ローカル標準時として扱うため以下のフォーマットで変換
  const date = new Date(`${day_str} ${time_str.replace(/-/g, ':')}`)
  return date
}

export default class ImageUploader {
  #files: File[] = []
  #upload_results: UploadResult[] = []
  #abort_controller: AbortController | undefined = undefined
  readonly #setUploadStatus: (upload_status: UploadStates) => void
  readonly #setResult: (upload_status: UploadResults) => void

  constructor (
    setUploadStates: (upload_states: UploadStates) => void,
    setResult: (upload_states: UploadResults) => void
  ) {
    this.#setUploadStatus = setUploadStates
    this.#setResult = setResult
  }

  async #upload () {
    while (this.#upload_results.length < this.#files.length) {
      const file = this.#files[this.#upload_results.length]
      const {width, height} = await imageFileToWidthHeight(file)
        .catch(e=> {
          console.error('onerror event: ', e)
          return {width: 0, height: 0}
        })
      if (width === 0 || height === 0) {
        this.#upload_results.push(upload_error.file_break)
        this.#setUploadStatus({files: this.#files, results: this.#upload_results})
        continue
      }
      const taken_at = fileNameToTakenTime(file.name)
      const last_modified = file.lastModified
      const utc_offset_minutes = taken_at?.getTimezoneOffset() || 0
      this.#abort_controller = new AbortController()
      const {error} = await PhotoAPI.uploadPhoto(
        file, width, height, last_modified, utc_offset_minutes,
        this.#abort_controller.signal
      )
      const error_type = error && (
        error.code === 1401 ? upload_error.storage_over :
        error.code === 1402 ? upload_error.size_upper :
        error.code === 1403 ? upload_error.size_lower :
        error.code === 1404 ? upload_error.file_format :
        error.code === 1405 ? upload_error.file_break :
        error.code === 1406 ? upload_error.file_count_over :
        upload_error.other
      )
      if (!error) trackFirstPhotoUploadSuccess()
      this.#upload_results.push(error_type)
      this.#setUploadStatus({
        files: [...this.#files], results: [...this.#upload_results]
      })
      if (this.#upload_results.filter(r=> r !== undefined).length >= MAX_ERROR_COUNT) {
        break
      }
    }
    this.#setResult(this.#upload_results.map((result, i) => ({file: this.#files[i], result})))
    this.#files = []
    this.#upload_results = []
    this.#setUploadStatus({files: [], results: []})
  }

  append (files: File[]) {
    // pushする前にアップロードが完了しているか確認
    const is_uploaded = this.#upload_results.length === this.#files.length
    // 画像及びサイズによりファイルを選別
    const uploadable_files: File[] = []
    const unuploadable_files: File[] = []
    const errors: UploadResult[] = []
    for (const file of files) {
      if (!UPLOAD_FILETYPE.includes(file.type)) {
        errors.push(upload_error.file_format)
        unuploadable_files.push(file)
      } else if (file.size < 64 * 1000) {
        errors.push(upload_error.size_lower)
        unuploadable_files.push(file)
      } else if (file.size > 100 * 1000 * 1000) { // MB
        errors.push(upload_error.size_upper)
        unuploadable_files.push(file)
      } else {
        uploadable_files.push(file)
      }
    }

    this.#files.push(...unuploadable_files, ...uploadable_files)
    this.#upload_results.push(...errors)
    this.#setUploadStatus({files: this.#files, results: this.#upload_results})
    // 二重アップロードを防ぐ
    if (is_uploaded) this.#upload()
  }

  cancel () {
    this.#files = this.#files.slice(0, this.#upload_results.length + 1)
    this.#abort_controller?.abort()
  }
}